import React, { useEffect, useState } from 'react';
import { portalApi, sessionHeaders, portalErrorMessage, saveBlob } from './portalApi';
import { usePortalGet } from './PortalOrdersPage';
import { askConfirm } from '../AppDialogs';
import CancelButton from '../CancelButton';

// The signed-in Customer's Quotes in the portal: the list at
// /portal/<name>/quotes (Active ones first, Show all for the rest), each Quote
// at /portal/<name>/quotes/<id> with its PDF, and Accept or Decline while
// it's Active. Every login on the Customer sees the same Quotes; a Draft or
// Withdrawn one never shows. Status, dates and amounts arrive already
// worked out and formatted (backend/services/portalQuoteService.js).

const CARD_STYLE = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 };
const INPUT_STYLE = { width: '100%', maxWidth: 320, padding: '8px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' };
const LINK_BUTTON = { background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' };
const MUTED = { fontSize: 14, color: '#5a6b85' };
const LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const TH = { padding: '8px 10px 8px 0', fontSize: 12, fontWeight: 600, color: '#5a6b85', textAlign: 'left', whiteSpace: 'nowrap' };
const TD = { padding: '10px 10px 10px 0', fontSize: 14, verticalAlign: 'top' };
const NUM = { textAlign: 'right' };
const SUB = { fontSize: 11, color: '#888' };
const SEARCH_DELAY_MS = 300;
const ACTION_BUTTON = { padding: '7px 12px', border: '1px solid #bfc8d6', color: '#1f2d3d', background: '#fff', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' };
const PRIMARY_BUTTON = { ...ACTION_BUTTON, border: 'none', color: '#fff', background: '#1976d2' };

const STATUS_COLOURS = {
  active: { background: '#e8f1fc', color: '#1565c0' },
  accepted: { background: '#f3e8fd', color: '#6a1b9a' },
  ordered: { background: '#e6f4ea', color: '#2e7d32' },
  declined: { background: '#eef1f5', color: '#3d4b63' },
  expired: { background: '#fff4e0', color: '#8a5a00' },
};

function StatusPill({ status, label }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', ...(STATUS_COLOURS[status] || STATUS_COLOURS.declined) }}>
      {label}
    </span>
  );
}

const quotesPath = (portalName) => `/api/portal-auth/portals/${encodeURIComponent(portalName)}/quotes`;

function QuoteList({ portalName, onOpen, onSessionEnded }) {
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  const params = { ...(search ? { search } : {}), ...(showAll ? { all: 'true' } : {}) };
  const { data: quotes, error } = usePortalGet(portalName, '/quotes', params, onSessionEnded, 'Could not load your quotes');

  let empty = 'You have no active quotes.';
  if (search) empty = 'No quotes match.';
  else if (showAll) empty = 'You have no quotes yet.';

  return (
    <div style={CARD_STYLE}>
      <h1 style={{ margin: '0 0 14px', fontSize: 20, fontWeight: 600 }}>Quotes</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
        <input
          type="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search quote number or PO reference"
          aria-label="Search quote number or PO reference"
          style={INPUT_STYLE}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Show accepted, declined and expired quotes
        </label>
      </div>
      {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
      {!error && quotes === null && <div style={MUTED}>Loading...</div>}
      {quotes && quotes.length === 0 && <div style={MUTED}>{empty}</div>}
      {quotes && quotes.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #d7dbe3' }}>
                <th style={TH}>Quote</th>
                <th style={TH}>Date</th>
                <th style={TH}>Valid until</th>
                <th style={TH}>PO reference</th>
                <th style={TH}>Status</th>
                <th style={{ ...TH, ...NUM }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr
                  key={q.id}
                  onClick={() => onOpen(q.id)}
                  title={`Open ${q.display_number}`}
                  style={{ borderBottom: '1px solid #eef1f5', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f8fc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ ...TD, fontWeight: 500, color: '#1976d2', whiteSpace: 'nowrap' }}>{q.display_number}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{q.quote_date}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{q.valid_until || '—'}</td>
                  <td style={TD}>{q.po_reference || '—'}</td>
                  <td style={TD}><StatusPill status={q.status} label={q.status_label} /></td>
                  <td style={{ ...TD, ...NUM, whiteSpace: 'nowrap' }}>
                    {q.total}
                    {q.total_excludes_shipping && <div style={SUB}>excl. shipping</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ minWidth: 140 }}>
      <div style={LABEL_STYLE}>{label}</div>
      <div style={{ fontSize: 14, marginTop: 2 }}>{children}</div>
    </div>
  );
}

function TotalRow({ label, value, strong }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 24,
      fontSize: strong ? 17 : 14, fontWeight: strong ? 700 : 400, padding: strong ? '8px 0' : '4px 0',
      ...(strong ? { borderTop: '1px solid #b3bbc7', marginTop: 4 } : {}),
    }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// Accept and Decline on an Active Quote. Decline asks for an optional reason
// in place before it's sent.
function QuoteDecision({ portalName, quote, onDecided, onSessionEnded }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  const run = async (action, body, fallback) => {
    setBusy(true);
    setError('');
    try {
      await portalApi.post(`${quotesPath(portalName)}/${quote.id}/${action}`, body, { headers: sessionHeaders(portalName) });
      onDecided();
      return;
    } catch (err) {
      if (err.response?.status === 401) {
        onSessionEnded();
        return;
      }
      setError(portalErrorMessage(err, fallback));
    }
    setBusy(false);
  };
  const accept = async () => {
    if (!(await askConfirm(`Accept quote ${quote.display_number} for ${quote.totals.total}? We'll prepare your order at these prices.`, { confirmLabel: 'Accept' }))) return;
    run('accept', null, 'Could not accept this quote');
  };
  const decline = () => run('decline', { note: reason }, 'Could not decline this quote');
  const off = { cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 };

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #eef1f5' }}>
      {declining ? (
        <>
          <label htmlFor="decline-reason" style={{ ...LABEL_STYLE, display: 'block', marginBottom: 6 }}>Reason (optional)</label>
          <textarea
            id="decline-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Let us know why, so we can do better next time"
            style={{ ...INPUT_STYLE, maxWidth: 520, resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <button type="button" onClick={decline} disabled={busy} title="Decline this quote" style={{ ...ACTION_BUTTON, color: '#b91c1c', borderColor: '#fecaca', ...off }}>Decline quote</button>
            <CancelButton onClick={() => { setDeclining(false); setError(''); }} disabled={busy} title="Close without declining" />
          </div>
        </>
      ) : (
        <>
          <div style={{ ...MUTED, fontSize: 13, marginBottom: 10 }}>
            {quote.valid_until ? `This quote is active until ${quote.valid_until}.` : 'This quote is active.'}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={accept} disabled={busy} title="Accept this quote" style={{ ...PRIMARY_BUTTON, ...off }}>Accept quote</button>
            <button type="button" onClick={() => setDeclining(true)} disabled={busy} title="Decline this quote" style={{ ...ACTION_BUTTON, ...off }}>Decline</button>
          </div>
        </>
      )}
      {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// What happened to a Quote that's no longer Active.
function QuoteOutcome({ quote, onNavigate }) {
  let text = null;
  if (quote.status === 'ordered' && quote.order) {
    return (
      <div style={{ ...MUTED, fontSize: 13, marginTop: 16, paddingTop: 14, borderTop: '1px solid #eef1f5' }}>
        You accepted this quote{quote.accepted_date ? ` on ${quote.accepted_date}` : ''}. It&apos;s now order{' '}
        <button type="button" onClick={() => onNavigate(`orders/${quote.order.id}`)} title={`Open order ${quote.order.display_number}`} style={LINK_BUTTON}>{quote.order.display_number}</button>.
      </div>
    );
  }
  if (quote.status === 'accepted' || quote.status === 'ordered') text = `You accepted this quote${quote.accepted_date ? ` on ${quote.accepted_date}` : ''}. We're preparing your order.`;
  else if (quote.status === 'declined') text = `This quote was declined${quote.declined_date ? ` on ${quote.declined_date}` : ''}.`;
  else if (quote.status === 'expired') text = `This quote expired${quote.valid_until ? ` on ${quote.valid_until}` : ''}. Contact us for a new one.`;
  if (!text) return null;
  return (
    <div style={{ ...MUTED, fontSize: 13, marginTop: 16, paddingTop: 14, borderTop: '1px solid #eef1f5' }}>
      {text}
      {quote.decline_reason && <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>Reason: {quote.decline_reason}</div>}
    </div>
  );
}

function DownloadButton({ portalName, quote, onSessionEnded }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const download = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await portalApi.get(`${quotesPath(portalName)}/${quote.id}/pdf`, { headers: sessionHeaders(portalName), responseType: 'blob' });
      saveBlob(res.data, `Quote ${quote.display_number}.pdf`);
    } catch (err) {
      if (err.response?.status === 401) {
        onSessionEnded();
        return;
      }
      setError('Could not prepare the PDF. Try again in a moment.');
    }
    setBusy(false);
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
      {error && <span style={{ color: '#b91c1c', fontSize: 13 }}>{error}</span>}
      <button type="button" onClick={download} disabled={busy} title="Save this quote as a PDF" style={{ ...ACTION_BUTTON, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>
        {busy ? 'Preparing...' : 'Download PDF'}
      </button>
    </span>
  );
}

function QuoteDetail({ portalName, quoteId, onBack, onNavigate, onSessionEnded }) {
  // Bumped after Accept or Decline, so the Quote reloads with its new status.
  const [version, setVersion] = useState(0);
  const { data: quote, error, notFound } = usePortalGet(portalName, `/quotes/${encodeURIComponent(quoteId)}`, { v: version }, onSessionEnded, 'Could not load this quote');
  const back = <button type="button" onClick={onBack} title="Show all your quotes" style={LINK_BUTTON}>‹ All quotes</button>;

  if (notFound) {
    return (
      <div style={CARD_STYLE}>
        <div style={{ marginBottom: 12 }}>{back}</div>
        <div style={MUTED}>This quote isn&apos;t available to you.</div>
      </div>
    );
  }
  if (error) return <div style={CARD_STYLE}><div style={{ marginBottom: 12 }}>{back}</div><div style={{ color: 'red', fontSize: 13 }}>{error}</div></div>;
  if (!quote) return <div style={CARD_STYLE}><div style={MUTED}>Loading...</div></div>;

  const { totals } = quote;
  return (
    <>
      <div style={CARD_STYLE}>
        <div style={{ marginBottom: 12 }}>{back}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{quote.display_number}</h1>
          <StatusPill status={quote.status} label={quote.status_label} />
          <DownloadButton portalName={portalName} quote={quote} onSessionEnded={onSessionEnded} />
        </div>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <Field label="Quote date">{quote.quote_date}</Field>
          <Field label="Valid until">{quote.valid_until || '—'}</Field>
          <Field label="PO reference">{quote.po_reference || '—'}</Field>
          <Field label="Delivery address">
            {quote.delivery_address.length ? quote.delivery_address.map((part) => <div key={part}>{part}</div>) : '—'}
          </Field>
          {quote.shipping && <Field label="Shipping">{quote.shipping}</Field>}
          {quote.comment && <Field label="Our comment"><div style={{ whiteSpace: 'pre-wrap', maxWidth: 420 }}>{quote.comment}</div></Field>}
        </div>
        {quote.can_decide
          ? <QuoteDecision portalName={portalName} quote={quote} onDecided={() => setVersion((v) => v + 1)} onSessionEnded={onSessionEnded} />
          : <QuoteOutcome quote={quote} onNavigate={onNavigate} />}
      </div>

      <div style={{ ...CARD_STYLE, marginTop: 16 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600 }}>Items</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #d7dbe3' }}>
                <th style={TH}>Product code</th>
                <th style={TH}>Description</th>
                <th style={TH}>Unit</th>
                <th style={{ ...TH, ...NUM }}>Qty</th>
                <th style={{ ...TH, ...NUM }}>Unit price{quote.price_suffix}</th>
                <th style={{ ...TH, ...NUM }}>Line total{quote.price_suffix}</th>
              </tr>
            </thead>
            <tbody>
              {quote.lines.map((line) => (
                <tr key={line.id} style={{ borderBottom: '1px solid #eef1f5' }}>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{line.product_code}</td>
                  <td style={TD}>{line.description}</td>
                  <td style={TD}>{line.unit}</td>
                  <td style={{ ...TD, ...NUM }}>{line.qty}</td>
                  <td style={{ ...TD, ...NUM, whiteSpace: 'nowrap' }}>{line.unit_price || '—'}</td>
                  <td style={{ ...TD, ...NUM, whiteSpace: 'nowrap' }}>{line.line_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <div style={{ width: '100%', maxWidth: 340 }}>
            {/* Business: Subtotal (ex GST), GST, Total (inc GST). Retail: Total
                (inc GST), then the GST it includes. */}
            {totals.subtotal && <TotalRow label={totals.subtotal_label} value={totals.subtotal} />}
            {totals.discount && <TotalRow label={totals.discount_label} value={totals.discount} />}
            <TotalRow label={totals.shipping_label} value={totals.shipping ?? 'To be confirmed'} />
            {totals.options && <TotalRow label={totals.options_label} value={totals.options} />}
            {totals.gst && quote.pricing_basis !== 'retail' && <TotalRow label={totals.gst_label} value={totals.gst} />}
            <TotalRow label={totals.total_label} value={totals.total} strong />
            {totals.gst && quote.pricing_basis === 'retail' && <TotalRow label={totals.gst_label} value={totals.gst} />}
          </div>
        </div>
      </div>
    </>
  );
}

// `quoteId` set shows that Quote; otherwise the list.
function PortalQuotesPage({ portalName, quoteId, onNavigate, onSessionEnded }) {
  if (quoteId) {
    return <QuoteDetail portalName={portalName} quoteId={quoteId} onBack={() => onNavigate('quotes')} onNavigate={onNavigate} onSessionEnded={onSessionEnded} />;
  }
  return <QuoteList portalName={portalName} onOpen={(id) => onNavigate(`quotes/${id}`)} onSessionEnded={onSessionEnded} />;
}

export default PortalQuotesPage;
